import { useEffect, useState } from "react";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import VoiceRecorder from "@/components/voice/VoiceRecorder";
import VoiceReviewWorkspace from "@/components/voice/VoiceReviewWorkspace";
import { useAuth } from "@/context/AuthContext";
import { clearVoiceSessionId, loadVoiceSessionId, saveVoiceSessionId } from "@/components/voice/voiceSessionPersistence.mjs";

const PROOF_CONTEXT = {
  source_product: "platform_shared", purpose: "voice_foundation_proof", context_reference: "owner_qa",
};

export default function VoiceFoundationProof() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [localFragments, setLocalFragments] = useState([]);
  const [uploaded, setUploaded] = useState({});
  const [session, setSession] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const [notice, setNotice] = useState("");
  const [resetToken, setResetToken] = useState(0);

  useEffect(() => {
    if (!user?.id) return undefined;
    let active = true;
    const restore = async () => {
      setBusy(true); setError("");
      try {
        const savedId = loadVoiceSessionId(user.id);
        const response = savedId
          ? await api.get(`/voice/sessions/${savedId}`)
          : await api.get("/voice/sessions/active", { params: PROOF_CONTEXT });
        if (!active) return;
        const restored = response.data;
        saveVoiceSessionId(user.id, restored.session_id);
        setSession(restored);
        setText(restored.user_edited_transcript);
        setReviewOpen(Boolean(restored.user_edited_transcript));
        setNotice("Текущая голосовая сессия восстановлена");
      } catch (problem) {
        if (problem?.response?.status !== 404 && active) setError(formatApiError(problem));
        if (problem?.response?.status === 404) clearVoiceSessionId(user.id);
      } finally { if (active) setBusy(false); }
    };
    restore();
    return () => { active = false; };
  }, [user?.id]);

  const ensureSession = async () => {
    if (session) return session;
    const { data } = await api.post("/voice/sessions", PROOF_CONTEXT);
    saveVoiceSessionId(user.id, data.session_id);
    setSession(data);
    return data;
  };

  const transcribe = async () => {
    if (!localFragments.length || busy) return;
    setBusy(true); setError(""); setNotice("");
    try {
      let current = await ensureSession();
      const mapped = { ...uploaded };
      for (const fragment of localFragments) {
        if (mapped[fragment.id]) continue;
        const form = new FormData();
        const extension = fragment.blob.type.includes("mp4") ? "m4a" : fragment.blob.type.includes("ogg") ? "ogg" : "webm";
        form.append("file", fragment.blob, `voice-${fragment.id}.${extension}`);
        const added = await api.post(`/voice/sessions/${current.session_id}/fragments`, form);
        current = added.data;
        const mediaId = current.fragments[current.fragments.length - 1].media_id;
        mapped[fragment.id] = mediaId;
        const result = await api.post(
          `/voice/sessions/${current.session_id}/fragments/${mediaId}/transcribe`,
          { operation_id: crypto.randomUUID() },
        );
        current = result.data;
      }
      saveVoiceSessionId(user.id, current.session_id);
      setUploaded(mapped); setSession(current); setText(current.user_edited_transcript);
      setReviewOpen(true);
    } catch (problem) {
      setError(formatApiError(problem));
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const { data } = await api.patch(`/voice/sessions/${session.session_id}/draft`, { text });
      setSession(data); setText(data.user_edited_transcript);
      setNotice("Исправления сохранены");
    }
    catch (problem) { setError(formatApiError(problem)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      await api.patch(`/voice/sessions/${session.session_id}/draft`, { text });
      const { data } = await api.post(`/voice/sessions/${session.session_id}/confirm`, { explicit_confirmation: true });
      setConfirmed(data); setReviewOpen(false);
    } catch (problem) { setError(formatApiError(problem)); }
    finally { setBusy(false); }
  };

  const restart = async () => {
    if (session?.session_id) {
      try { await api.post(`/voice/sessions/${session.session_id}/abandon`); }
      catch (problem) { setError(formatApiError(problem)); return; }
    }
    clearVoiceSessionId(user.id);
    setSession(null); setUploaded({}); setLocalFragments([]); setText(""); setConfirmed(null);
    setNotice(""); setReviewOpen(false); setResetToken((value) => value + 1);
  };

  return <main className="min-h-screen bg-[#F6EFE4] px-4 py-5 sm:px-8 sm:py-8" data-testid="voice-foundation-proof">
    <div className="mx-auto max-w-5xl">
      <button type="button" onClick={() => nav("/hall")} className="inline-flex min-h-11 items-center gap-2 text-sm text-[#6F5944]"><ChevronLeft size={18} />В главный зал</button>
      <div className="mt-5 max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#937556]">Anokina Studio · Owner proof</div>
        <h1 className="mt-2 font-serif text-4xl text-[#2A1D14] sm:text-5xl">Voice Foundation</h1>
        <p className="mt-4 text-sm leading-6 text-[#66513E]">Запиши один или несколько фрагментов. Расшифровка начнётся только после завершения записи и твоего явного действия.</p>
      </div>
      <div className="mt-8"><VoiceRecorder onFragmentsChange={setLocalFragments} resetToken={resetToken} disabled={busy} /></div>
      {error && <div className="mt-4 rounded-xl border border-[#D8A89F] bg-[#FFF4F1] p-4 text-sm text-[#8B382E]" role="alert">{error}</div>}
      {notice && <div className="mt-4 rounded-xl border border-[#BCD0B5] bg-[#F3FAF0] p-4 text-sm text-[#395333]" role="status">{notice}</div>}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="button" disabled={busy || !localFragments.length} onClick={transcribe} className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#4A3728] px-6 text-sm text-white disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />}Расшифровать и проверить</button>
        {session?.user_edited_transcript && <button type="button" onClick={() => setReviewOpen(true)} className="min-h-12 rounded-xl border border-[#D8C7AE] px-5 text-sm">Открыть проверку текста</button>}
      </div>
      {confirmed && <div className="mt-6 rounded-2xl border border-[#BCD0B5] bg-[#F3FAF0] p-5 text-sm text-[#395333]">Подтверждённая версия V{confirmed.version} сохранена. Старые версии остаются в истории.</div>}
    </div>
    <VoiceReviewWorkspace open={reviewOpen} session={session} text={text} onText={(value) => { setText(value); setNotice(""); }} busy={busy} notice={notice}
      onListen={() => { const button = document.querySelector('[data-testid="private-audio-player"] button'); button?.click(); }}
      onAdd={() => setReviewOpen(false)} onSave={save} onConfirm={confirm} onRestart={restart} />
  </main>;
}
